import { useEffect, useState } from 'react'
import { AlertTriangle, HandCoins } from 'lucide-react'
import { formatDateTimeCompact } from '@/lib/datetime'
import { formatMinorCurrency } from '@/lib/format'
import { queryKeys } from '@/lib/query-client'
import type { CustomerPenalty } from '@/types/api'
import { useAdminMutation } from '@/hooks/useAdminMutation'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { KeyValue, KeyValueGrid } from '@/components/ui/RowActions'
import { waivePenalty } from './api'
import styles from './PenaltiesPage.module.css'

interface WaivePenaltyDialogProps {
  penalty: CustomerPenalty | null
  onClose: () => void
}

/**
 * Forgives one penalty.
 *
 * The reason is required and permanent, because this is the record an operator
 * will be asked about later — and because without a waive path at all, the only
 * remedy for a disputed penalty is a manual `UPDATE` against the database,
 * which leaves no trace of who decided or why.
 */
export function WaivePenaltyDialog({ penalty, onClose }: WaivePenaltyDialogProps) {
  const open = penalty !== null
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (open) setReason('')
  }, [open, penalty?.id])

  const reasonError =
    reason.trim().length > 0 && reason.trim().length < 3
      ? 'Give at least a few words — this is the permanent record of the decision.'
      : undefined

  const mutation = useAdminMutation({
    mutationFn: () => waivePenalty(penalty!.id, { reason: reason.trim() }),
    successTitle: 'Penalty waived',
    successDescription: (waived) =>
      `${formatMinorCurrency(waived.amountMinor, waived.currency)} will no longer be charged to ${
        waived.customerName ?? 'this customer'
      }.`,
    errorTitle: 'The penalty was not waived',
    invalidate: [queryKeys.penalties.all, queryKeys.dashboard.all],
    onSuccess: () => onClose(),
  })

  const canSubmit = reason.trim().length >= 3 && !mutation.isPending

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Waive this penalty?"
      size="sm"
      busy={mutation.isPending}
      description="The customer will no longer owe this amount, and it will not be added to their next booking."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Keep the charge
          </Button>
          <Button
            variant="danger"
            iconLeft={<HandCoins aria-hidden="true" />}
            loading={mutation.isPending}
            disabled={!canSubmit}
            onClick={() => mutation.mutate(undefined)}
          >
            {mutation.isPending ? 'Waiving…' : 'Waive penalty'}
          </Button>
        </>
      }
    >
      {penalty ? (
        <div className={styles.dialogBody}>
          <section className={styles.summary}>
            <KeyValueGrid>
              <KeyValue label="Customer">{penalty.customerName ?? `#${penalty.userId}`}</KeyValue>
              <KeyValue label="Amount">
                <strong className={styles.amount}>
                  {formatMinorCurrency(penalty.amountMinor, penalty.currency)}
                </strong>
              </KeyValue>
              <KeyValue label="From booking">{penalty.sourceBookingReference ?? '—'}</KeyValue>
              <KeyValue label="Cancelled">{formatDateTimeCompact(penalty.createdAt)}</KeyValue>
            </KeyValueGrid>
            <p className={styles.note}>{penalty.reason}</p>
          </section>

          <div className={styles.warning} role="note">
            <AlertTriangle aria-hidden="true" />
            <p>
              This is recorded permanently against your account. It cannot be undone from here — a waived
              penalty is never re-charged automatically.
            </p>
          </div>

          <Field
            label="Reason for waiving"
            required
            error={reasonError}
            hint="Shown in the penalty ledger alongside your name."
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                value={reason}
                maxLength={500}
                disabled={mutation.isPending}
                placeholder="Customer disputed — hospital admission confirmed…"
                onChange={(event) => setReason(event.target.value)}
              />
            )}
          </Field>
        </div>
      ) : null}
    </Dialog>
  )
}
